import type { CncSpeciesAssignment, RfqProject, RfqSubmission, SupplierOrg, SupplierShipToAddress, SupplierShipToPreset, TimberRfqDrynessSpec, TimberSupplierInvite, TimberSupplierStructureRef } from '../types';
import { TIMBER_SPECIES_INFO } from '../types/timberPricing';
import { sanitizeCncSpeciesAssignments } from './cncRfqAssignments';
import type { TimberItem } from './materialsDatabaseService';
import type { TimberSpeciesDrynessEntry } from './supplierTimberExport';
import { normalizeTimberRfqDrynessSpec, resolveTimberRfqDrynessSpec, type SelectableTimberRfqDrynessSpec } from './supplierTimberExport';
import { speciesLabelForMatching } from './timberMaterialParser';
import { normalizeStructureSpecies } from './timberRfqStructureSpecies';
import { normalizeSupplierOrg, orgHasCapability } from './vendorHelpers';

function inviteForOrg(
  project: Pick<RfqProject, 'timberSupplierInvites'>,
  orgId: string,
): TimberSupplierInvite | undefined {
  return project.timberSupplierInvites?.find(invite => invite.supplierOrgId === orgId);
}

export function rfqProjectTimberSpeciesDryness(
  project: Pick<RfqProject, 'timberSupplierInvites' | 'timberDrynessSpec'>,
  orgId: string,
  species: string,
): SelectableTimberRfqDrynessSpec {
  const row = inviteForOrg(project, orgId);
  const perSpecies = row?.speciesDryness?.[species];
  if (perSpecies) return normalizeTimberRfqDrynessSpec(perSpecies);
  if (row?.drynessSpec) return normalizeTimberRfqDrynessSpec(row.drynessSpec);
  return resolveTimberRfqDrynessSpec(project.timberDrynessSpec);
}

/** @deprecated Use {@link rfqProjectTimberSpeciesDrynessEntries} or per-species dryness. */
export function rfqProjectTimberSupplierDryness(
  project: Pick<RfqProject, 'timberSupplierInvites' | 'timberDrynessSpec'>,
  orgId: string,
): SelectableTimberRfqDrynessSpec {
  const species = rfqProjectTimberSupplierSpecies(project, orgId);
  if (!species.length) return resolveTimberRfqDrynessSpec(project.timberDrynessSpec);
  const entries = rfqProjectTimberSpeciesDrynessEntries(project, orgId);
  const unique = new Set(entries.map(entry => entry.drynessSpec));
  if (unique.size === 1) return entries[0].drynessSpec;
  return entries[0]?.drynessSpec ?? resolveTimberRfqDrynessSpec(project.timberDrynessSpec);
}

export function rfqProjectTimberSpeciesDrynessEntries(
  project: Pick<RfqProject, 'timberSupplierInvites' | 'timberDrynessSpec'>,
  orgId: string,
): TimberSpeciesDrynessEntry[] {
  return rfqProjectTimberSupplierSpecies(project, orgId).map(species => ({
    species,
    drynessSpec: rfqProjectTimberSpeciesDryness(project, orgId, species),
  }));
}

export function timberSupplierInvitesWithSpeciesDryness(
  invites: TimberSupplierInvite[] | undefined,
  orgId: string,
  species: string,
  drynessSpec: TimberRfqDrynessSpec,
): TimberSupplierInvite[] {
  const normalizedDryness = normalizeTimberRfqDrynessSpec(drynessSpec);
  const list = invites ?? [];
  const idx = list.findIndex(invite => invite.supplierOrgId === orgId);
  if (idx >= 0) {
    return list.map((invite, i) => {
      if (i !== idx) return invite;
      return {
        ...invite,
        speciesDryness: {
          ...(invite.speciesDryness ?? {}),
          [species]: normalizedDryness,
        },
      };
    });
  }
  return [
    ...list,
    {
      supplierOrgId: orgId,
      requestedSpecies: [species],
      speciesDryness: { [species]: normalizedDryness },
    },
  ];
}

export function rfqProjectTimberSupplierSpecies(
  project: Pick<RfqProject, 'timberSupplierInvites'>,
  orgId: string,
): string[] {
  const row = inviteForOrg(project, orgId);
  return row?.requestedSpecies?.filter(Boolean) ?? [];
}

/** Union of species lists, sorted for display. */
export function mergeSupplierSpeciesLists(...lists: string[][]): string[] {
  const set = new Set<string>();
  for (const list of lists) {
    for (const species of list) {
      const trimmed = species?.trim();
      if (trimmed) set.add(trimmed);
    }
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

/**
 * CNC supplier species, optionally inheriting requested species from the timber RFQ
 * on the same deal when the supplier offers both timber and CNC.
 */
export function rfqProjectCncSupplierSpecies(
  cncProject: Pick<RfqProject, 'timberSupplierInvites'>,
  timberProject: Pick<RfqProject, 'timberSupplierInvites'> | null | undefined,
  orgId: string,
  inheritTimberSpecies: boolean,
): string[] {
  const cncSpecies = rfqProjectTimberSupplierSpecies(cncProject, orgId);
  if (!inheritTimberSpecies || !timberProject) return cncSpecies;
  const timberSpecies = rfqProjectTimberSupplierSpecies(timberProject, orgId);
  return mergeSupplierSpeciesLists(cncSpecies, timberSpecies);
}

/** Dual timber + CNC vendors inherit timber RFQ species on the CNC work order. */
export function supplierInheritsTimberSpeciesForCnc(org: SupplierOrg | undefined | null): boolean {
  if (!org) return false;
  const norm = normalizeSupplierOrg(org);
  return orgHasCapability(norm, 'timber') && orgHasCapability(norm, 'cnc');
}

/** Always offered on RFQ species pickers, even with no catalog rows yet. */
const ALWAYS_SELECTABLE_TIMBER_SPECIES = ['Grain-Matched Glulam'];

export function allSelectableTimberSpecies(timberItems: TimberItem[], extra: string[] = []): string[] {
  const set = new Set<string>(ALWAYS_SELECTABLE_TIMBER_SPECIES);
  for (const item of timberItems) {
    const species = item.species?.trim();
    if (species) set.add(species);
  }
  for (const info of Object.values(TIMBER_SPECIES_INFO)) {
    set.add(info.name);
  }
  for (const species of extra) {
    const trimmed = species?.trim();
    if (trimmed) set.add(trimmed);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

function resolveInviteSpeciesDryness(
  species: string,
  existing: TimberSupplierInvite | undefined,
  orgDryness: Record<string, TimberRfqDrynessSpec> | undefined,
  projectDefaultDryness?: TimberRfqDrynessSpec | null,
): TimberRfqDrynessSpec {
  const raw =
    orgDryness?.[species]
    ?? existing?.speciesDryness?.[species]
    ?? existing?.drynessSpec
    ?? projectDefaultDryness;
  return normalizeTimberRfqDrynessSpec(raw);
}

export type TimberSupplierStructureScopeInput = {
  scope: 'all' | 'selected';
  structures: TimberSupplierStructureRef[];
};

/** Lowercased, deduped recipient emails; undefined when empty (= everyone). */
function cleanNotifyEmails(raw: string[] | null | undefined): string[] | undefined {
  if (!raw?.length) return undefined;
  const out = Array.from(
    new Set(raw.map(e => (typeof e === 'string' ? e.trim().toLowerCase() : '')).filter(e => e.includes('@'))),
  ).sort();
  return out.length ? out : undefined;
}

export function buildTimberSupplierInvites(
  selectedOrgIds: string[],
  speciesByOrg: Record<string, string[]>,
  existingInvites?: TimberSupplierInvite[],
  drynessByOrg?: Record<string, Record<string, TimberRfqDrynessSpec>>,
  projectDefaultDryness?: TimberRfqDrynessSpec | null,
  shipToByOrg?: Record<string, { preset: SupplierShipToPreset; custom?: SupplierShipToAddress }>,
  structureScopeByOrg?: Record<string, TimberSupplierStructureScopeInput>,
  cncAssignmentsByOrg?: Record<string, CncSpeciesAssignment[]>,
  /**
   * Species per structure (structure id → species). An entry (even null) replaces the
   * invite's map; no entry keeps the existing one, re-normalized against the new species.
   */
  structureSpeciesByOrg?: Record<string, Record<string, string[]> | null | undefined>,
  /**
   * Who receives RFQ emails (lowercase). An entry replaces the invite's list (null /
   * empty = everyone); no entry keeps the existing one.
   */
  notifyEmailsByOrg?: Record<string, string[] | null | undefined>,
): TimberSupplierInvite[] {
  const existingByOrg = new Map((existingInvites ?? []).map(invite => [invite.supplierOrgId, invite]));
  return selectedOrgIds.map(supplierOrgId => {
    const existing = existingByOrg.get(supplierOrgId);
    const requestedSpecies = Array.from(new Set(speciesByOrg[supplierOrgId]?.filter(Boolean) ?? [])).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' }),
    );
    const orgDryness = drynessByOrg?.[supplierOrgId];
    const speciesDryness: Record<string, TimberRfqDrynessSpec> = {};
    for (const sp of requestedSpecies) {
      speciesDryness[sp] = resolveInviteSpeciesDryness(sp, existing, orgDryness, projectDefaultDryness);
    }
    const shipToConfig = shipToByOrg?.[supplierOrgId];
    const shipToPreset = shipToConfig?.preset ?? existing?.shipToPreset;
    const shipToCustom =
      shipToConfig?.preset === 'custom'
        ? shipToConfig.custom
        : shipToPreset === 'custom'
          ? existing?.shipToCustom
          : undefined;
    const scopeInput = structureScopeByOrg?.[supplierOrgId];
    const structureScope = scopeInput
      ? scopeInput.scope
      : existing?.structureScope;
    const structures = scopeInput
      ? scopeInput.structures
      : existing?.structures;
    const scoped = structureScope === 'selected' && (structures?.length ?? 0) > 0;
    // CNC species assignments: an entry in cncAssignmentsByOrg (even []) replaces the
    // existing assignments for that org; no entry preserves whatever the invite had.
    const cncAssignments = sanitizeCncSpeciesAssignments(
      cncAssignmentsByOrg && supplierOrgId in cncAssignmentsByOrg
        ? cncAssignmentsByOrg[supplierOrgId]
        : existing?.cncSpeciesAssignments,
    );
    const structureSpecies = normalizeStructureSpecies(
      structureSpeciesByOrg && supplierOrgId in structureSpeciesByOrg
        ? structureSpeciesByOrg[supplierOrgId] ?? undefined
        : existing?.structureSpecies,
      requestedSpecies,
    );
    const notifyEmails = cleanNotifyEmails(
      notifyEmailsByOrg && supplierOrgId in notifyEmailsByOrg
        ? notifyEmailsByOrg[supplierOrgId]
        : existing?.notifyEmails,
    );
    return {
      supplierOrgId,
      requestedSpecies,
      ...(requestedSpecies.length ? { speciesDryness } : {}),
      ...(shipToPreset ? { shipToPreset } : {}),
      // Persist custom delivery even when Street is still empty so the radio choice survives reload.
      ...(shipToPreset === 'custom' && shipToCustom ? { shipToCustom } : {}),
      ...(scoped ? { structureScope: 'selected' as const, structures } : {}),
      ...(structureSpecies ? { structureSpecies } : {}),
      ...(notifyEmails ? { notifyEmails } : {}),
      ...(cncAssignments.length ? { cncSpeciesAssignments: cncAssignments } : {}),
    };
  });
}

export function mergeTimberSupplierInviteForOrg(
  orgId: string,
  requestedSpecies: string[],
  speciesDryness: Record<string, TimberRfqDrynessSpec>,
  existingInvites?: TimberSupplierInvite[],
): TimberSupplierInvite[] {
  const sorted = Array.from(new Set(requestedSpecies.map(s => s.trim()).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  );
  const existingInvite = (existingInvites ?? []).find(invite => invite.supplierOrgId === orgId);
  const dryness: Record<string, TimberRfqDrynessSpec> = {};
  for (const species of sorted) {
    const raw = speciesDryness[species] ?? existingInvite?.speciesDryness?.[species];
    dryness[species] = normalizeTimberRfqDrynessSpec(raw);
  }
  const others = (existingInvites ?? []).filter(invite => invite.supplierOrgId !== orgId);
  return [
    ...others,
    {
      supplierOrgId: orgId,
      requestedSpecies: sorted,
      speciesDryness: dryness,
      // Quote edits only touch species/dryness — keep delivery + structure + CNC invite fields.
      ...(existingInvite?.shipToPreset ? { shipToPreset: existingInvite.shipToPreset } : {}),
      ...(existingInvite?.shipToCustom ? { shipToCustom: existingInvite.shipToCustom } : {}),
      ...(existingInvite?.structureScope === 'selected' && existingInvite.structures?.length
        ? { structureScope: 'selected' as const, structures: existingInvite.structures }
        : {}),
      ...(() => {
        const kept = normalizeStructureSpecies(existingInvite?.structureSpecies, sorted);
        return kept ? { structureSpecies: kept } : {};
      })(),
      ...(() => {
        const kept = cleanNotifyEmails(existingInvite?.notifyEmails);
        return kept ? { notifyEmails: kept } : {};
      })(),
      ...(existingInvite?.cncSpeciesAssignments?.length
        ? { cncSpeciesAssignments: existingInvite.cncSpeciesAssignments }
        : {}),
    },
  ];
}

export function speciesLabelMatchesRequested(label: string, requestedSpecies: string[]): boolean {
  const normalizedLabel = speciesLabelForMatching(label).trim().toLowerCase();
  if (!requestedSpecies.length) return true;
  if (!normalizedLabel) return false;
  for (const req of requestedSpecies) {
    const normalizedReq = speciesLabelForMatching(req).trim().toLowerCase();
    if (!normalizedReq) continue;
    if (normalizedLabel === normalizedReq) return true;
    if (normalizedLabel.includes(normalizedReq) || normalizedReq.includes(normalizedLabel)) return true;
    for (const alias of buildSpeciesFileNameAliases(req)) {
      if (alias.length >= 2) {
        const compact = normalizedLabel.replace(/\s+/g, '');
        if (compact.includes(alias) || alias.includes(compact)) return true;
      }
    }
  }
  return false;
}

/** Best-effort species match from a quote PDF filename (longest catalog name wins). */
export function buildSpeciesFileNameAliases(species: string): string[] {
  const trimmed = species.trim();
  if (!trimmed) return [];
  const lower = trimmed.toLowerCase();
  const aliases = new Set<string>([lower, lower.replace(/\s+/g, ''), lower.replace(/\s+/g, '-')]);
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    aliases.add(words.map(w => w[0]!.toLowerCase()).join(''));
    aliases.add((words[0]![0]! + words.slice(1).join('')).toLowerCase());
  }
  return Array.from(aliases).filter(a => a.length >= 2).sort((a, b) => b.length - a.length);
}

export function inferQuotedSpeciesFromFileName(fileName: string, speciesCatalog: string[]): string | null {
  const hay = normalizeFileNameForSpeciesMatch(fileName);
  if (!hay) return null;

  const sorted = [...speciesCatalog]
    .filter(s => s.trim())
    .sort((a, b) => b.length - a.length);
  for (const species of sorted) {
    if (hay.includes(species.toLowerCase())) return species;
  }

  const aliasMatches: Array<{ species: string; alias: string }> = [];
  for (const species of sorted) {
    for (const alias of buildSpeciesFileNameAliases(species)) {
      aliasMatches.push({ species, alias });
    }
  }
  aliasMatches.sort((a, b) => b.alias.length - a.alias.length);
  for (const { species, alias } of aliasMatches) {
    const pattern = new RegExp(`(?:^|[\\s._-])${escapeRegExp(alias)}(?:$|[\\s._-])`);
    if (pattern.test(hay)) return species;
  }
  return null;
}

function normalizeFileNameForSpeciesMatch(fileName: string): string {
  return fileName.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function submissionQuotedSpecies(
  sub: Partial<Pick<RfqSubmission, 'quotedSpecies' | 'fileName'>>,
  speciesCatalog: string[] = [],
): string | null {
  const tagged = sub.quotedSpecies?.trim();
  if (tagged) return tagged;
  return inferQuotedSpeciesFromFileName(sub.fileName ?? '', speciesCatalog);
}

/** Dryness for a quote row — submission value wins over RFQ invite defaults. */
export function submissionQuotedDryness(
  sub: Partial<Pick<RfqSubmission, 'quotedDryness' | 'legacyImport'>>,
  project: Pick<RfqProject, 'timberSupplierInvites' | 'timberDrynessSpec'>,
  orgId: string,
  species: string,
): SelectableTimberRfqDrynessSpec {
  if (sub.quotedDryness) return normalizeTimberRfqDrynessSpec(sub.quotedDryness);
  if (species) return rfqProjectTimberSpeciesDryness(project, orgId, species);
  return resolveTimberRfqDrynessSpec(project.timberDrynessSpec);
}

/** Union of RFQ-requested species and species with quote submissions on file. */
export function rfqProjectTimberSupplierDisplaySpecies(
  project: Pick<RfqProject, 'timberSupplierInvites'>,
  orgId: string,
  submissions: Array<
    Partial<Pick<RfqSubmission, 'quotedSpecies' | 'fileName' | 'notBidding' | 'storagePath' | 'cncQuote'>>
  > = [],
  speciesCatalog: string[] = [],
): string[] {
  const requested = rfqProjectTimberSupplierSpecies(project, orgId);
  const quoted: string[] = [];
  for (const sub of submissions) {
    if (sub.notBidding || (!sub.storagePath && !sub.cncQuote)) continue;
    const species = submissionQuotedSpecies(sub, speciesCatalog);
    if (species) quoted.push(species);
  }
  return mergeSupplierSpeciesLists(requested, quoted);
}

export function timberSupplierInvitesWithoutOrg(
  invites: TimberSupplierInvite[] | undefined,
  orgId: string,
): TimberSupplierInvite[] {
  return (invites ?? []).filter(invite => invite.supplierOrgId !== orgId);
}
